// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import { Icon } from '@/components/common/icons';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import type { ResponsiveStyleValue } from '@mui/system';

type CssSize = string | number;

export type UnderlineHoverCtaProps = {
	href: string;
	label: string;
	color?: string;
	typographyVariant?: TypographyProps['variant'];
	fontSize?: ResponsiveStyleValue<CssSize>;
	fontWeight?: ResponsiveStyleValue<number>;
	lineHeight?: ResponsiveStyleValue<CssSize>;
	letterSpacing?: ResponsiveStyleValue<CssSize>;
	typographySx?: SxProps<Theme>;
	activeOnParentHoverSelector?: string;
	truncateLabel?: boolean;
	underlineThickness?: CssSize;
	/** When set, used for the animated underline bar (defaults to primary palette). */
	underlineColor?: string;
	underlineBaseOpacity?: number;
	underlineHoverOpacity?: number;
	arrowShiftPx?: number;
	paddingBottom?: CssSize;
	underlineOffsetBottom?: number;
};

const UnderlineHoverCta = ({
	href,
	label,
	color,
	typographyVariant = 'body-lg' as const,
	fontSize,
	fontWeight = 500,
	lineHeight,
	letterSpacing,
	typographySx,
	activeOnParentHoverSelector,
	truncateLabel = false,
	underlineThickness = '2px',
	underlineColor,
	underlineBaseOpacity = 0,
	underlineHoverOpacity = 1,
	arrowShiftPx = 4,
	paddingBottom = '6px',
	underlineOffsetBottom = 0
}: UnderlineHoverCtaProps) => {
	const theme = useTheme();

	const baseTypographySx: SxProps<Theme> = {
		color: color ?? theme.palette.common.white,
		fontSize,
		fontWeight,
		lineHeight,
		letterSpacing
	};

	const mergedTypographySx: SxProps<Theme> = [
		baseTypographySx,
		...(Array.isArray(typographySx) ? typographySx : typographySx ? [typographySx] : [])
	];

	return (
		<Link href={href} passHref>
			<Box
				component="span"
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 2,
					maxWidth: '100%',
					minWidth: 0,
					position: 'relative',
					cursor: 'pointer',
					// Space for the hover line
					pb: paddingBottom,
					'&::after': {
						content: '""',
						position: 'absolute',
						left: 0,
						right: 0,
						bottom: underlineOffsetBottom === 0 ? 0 : `${underlineOffsetBottom}px`,
						height: underlineThickness,
						backgroundColor:
							underlineColor ??
							theme.palette.primary.lighter ??
							theme.palette.primary.light ??
							theme.palette.primary.main,
						borderRadius: 999,
						transform: 'scaleX(0)',
						transformOrigin: 'left',
						opacity: underlineBaseOpacity,
						transition: 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)',
						pointerEvents: 'none'
					},
					'&:hover::after, &:focus-within::after': {
						transform: 'scaleX(1)',
						opacity: underlineHoverOpacity
					},
					'& .UnderlineHoverCta-arrow': {
						display: 'inline-block',
						transform: 'translateX(0)',
						transition: 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)'
					},
					'&:hover .UnderlineHoverCta-arrow, &:focus-within .UnderlineHoverCta-arrow': {
						transform: `translateX(${arrowShiftPx}px)`
					},
					...(activeOnParentHoverSelector
						? {
								[`${activeOnParentHoverSelector}:hover &::after, ${activeOnParentHoverSelector}:focus-within &::after`]:
									{
										transform: 'scaleX(1)',
										opacity: 1
									},
								[`${activeOnParentHoverSelector}:hover & .UnderlineHoverCta-arrow, ${activeOnParentHoverSelector}:focus-within & .UnderlineHoverCta-arrow`]:
									{
										transform: `translateX(${arrowShiftPx}px)`
									}
							}
						: {}),
					'@media (prefers-reduced-motion: reduce)': {
						'&::after': { transition: 'none' },
						'& .UnderlineHoverCta-arrow': { transition: 'none' }
					}
				}}
			>
				<Icon
					name="arrowSmallRight"
					aria-hidden="true"
					className="UnderlineHoverCta-arrow"
					width={11}
					height={9}
					color={color ?? theme.palette.common.white}
				/>
				<Typography variant={typographyVariant} component="span" sx={mergedTypographySx}>
					<Box
						component="span"
						sx={
							truncateLabel
								? {
										display: 'block',
										maxWidth: '100%',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									}
								: undefined
						}
					>
						{label}
					</Box>
				</Typography>
			</Box>
		</Link>
	);
};

export default UnderlineHoverCta;
